// RequestWithdrawalForm — agent submits a withdrawal request from
// their OWN pool. Inline collapsible card embedded in MyWalletPanel.
//
// Flow: agent specifies amount + UPI ID; submit holds the amount
// immediately (debit kind='withdrawal_hold' on agent_wallet_ledger)
// so it can't be double-spent. Admin pays manually via UPI and
// marks paid → held amount becomes final. If rejected, the hold
// refunds back.
// POST /api/admin/me-agent/withdraw — same endpoint the legacy
// WithdrawModal hit; only the wrapping presentation changed.
//
// Wallet sync: the hold IS a wallet movement (debit), so refresh the
// global chip on success — chrome stays in lockstep.

import { FormEvent, useState } from 'react';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { useT } from '@baaligames/shared-ui/lib/i18n';

type Props = {
  /** Current pool balance — used for max-amount validation. */
  balance: number;
  onSubmitted: () => void;
  onCancel: () => void;
  /** Phase 10: which wallet is being withdrawn from. */
  target?: 'wallet' | 'deposit';
};

export default function RequestWithdrawalForm({
  balance, onSubmitted, onCancel, target = 'wallet',
}: Props) {
  const t = useT();
  const [amount, setAmount] = useState('');
  const [upi, setUpi] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [note, setNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'cash'>('upi');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const formattedBalance = `₹${balance.toLocaleString('en-IN')}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const amt = Number(amount);
    if (!Number.isInteger(amt) || amt < 10) {
      setErr(t('agent.withdrawal.error.amountMin')); setBusy(false); return;
    }
    if (amt > balance) {
      setErr(t('agent.withdrawal.error.amountOverflow', { balance: formattedBalance })); setBusy(false); return;
    }
    if (paymentMethod === 'upi' && upi.trim().length < 3) {
      setErr(t('agent.withdrawal.error.upi')); setBusy(false); return;
    }
    try {
      await adminApi.post('/admin/me-agent/withdraw', {
        amount: amt,
        // For cash payouts the agent fills in a meeting marker
        // instead of a UPI handle (the backend still requires the
        // field; the parent reads it as a note).
        upi_id: paymentMethod === 'cash'
          ? (upi.trim() || 'CASH (in-person)')
          : upi.trim(),
        whatsapp_number: whatsapp.trim() || undefined,
        note: note.trim() || undefined,
        target,
        payment_method: paymentMethod,
      });
      // The submit places a hold on the right wallet — visible as a
      // ledger movement. Refresh the global chip immediately for
      // wallet target; deposit chip refresh happens via SSE alert.
      if (target === 'wallet') refreshAgentWallet();
      setAmount(''); setUpi(''); setWhatsapp(''); setNote('');
      onSubmitted();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.22 }}
      className="overflow-hidden rounded-2xl
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      <div className={`flex items-start gap-2.5 px-4 py-3 border-b border-arena-200/40 dark:border-arena-600/40
                      ${target === 'deposit'
                        ? 'bg-amber-50/70 dark:bg-amber-400/10'
                        : 'bg-brand-50/70 dark:bg-brand-400/10'}`}>
        <Icon name="arrow_downward" size={16}
              className={`${target === 'deposit'
                ? 'text-amber-700 dark:text-amber-300'
                : 'text-brand-700 dark:text-brand-300'} mt-0.5 shrink-0`} filled />
        <div className="min-w-0 flex-1">
          <div className={`text-[11px] uppercase tracking-wider font-bold ${target === 'deposit'
              ? 'text-amber-700 dark:text-amber-300'
              : 'text-brand-700 dark:text-brand-300'}`}>
            {t(target === 'deposit' ? 'agent.withdrawal.banner.eyebrow.deposit' : 'agent.withdrawal.banner.eyebrow.wallet')}
          </div>
          <div className="text-[12.5px] text-arena-700 dark:text-neon-100/90 leading-snug">
            {t(target === 'deposit' ? 'agent.withdrawal.banner.body.deposit' : 'agent.withdrawal.banner.body.wallet')}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="grid place-items-center w-7 h-7 rounded-full
                     hover:bg-arena-100/60 dark:hover:bg-arena-600/55 transition shrink-0"
          aria-label={t('agent.withdrawal.banner.closeAria')}
        >
          <Icon name="close" size={15} className="text-arena-600 dark:text-neon-200/70" />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* How does the agent want to be paid? Same UPI/Cash toggle
            shape used on recharge. Cash means the parent will hand
            over notes off-platform; UPI means a digital transfer. */}
        <div>
          <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
            {t('agent.withdrawal.method.eyebrow')}
          </span>
          <div className="grid grid-cols-2 gap-2">
            <PayMethodPill
              active={paymentMethod === 'upi'}
              onClick={() => setPaymentMethod('upi')}
              icon="qr_code_2"
              label={t('agent.withdrawal.method.upi.label')}
              note={t('agent.withdrawal.method.upi.note')}
            />
            <PayMethodPill
              active={paymentMethod === 'cash'}
              onClick={() => setPaymentMethod('cash')}
              icon="payments"
              label={t('agent.withdrawal.method.cash.label')}
              note={t('agent.withdrawal.method.cash.note')}
            />
          </div>
        </div>
        <Field label={t('agent.withdrawal.field.amount.label', { balance: formattedBalance })}>
          <input
            type="number" inputMode="numeric"
            className={`${inputCls} tabular-nums`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t('agent.withdrawal.field.amount.placeholder')} min={10} max={balance} step={1}
          />
        </Field>
        <Field label={paymentMethod === 'cash'
          ? t('agent.withdrawal.field.cashMarker.label')
          : t('agent.withdrawal.field.upi.label')}>
          <input
            className={`${inputCls} font-mono`}
            value={upi}
            onChange={(e) => setUpi(e.target.value)}
            placeholder={paymentMethod === 'cash'
              ? t('agent.withdrawal.field.cashMarker.placeholder')
              : t('agent.withdrawal.field.upi.placeholder')}
            maxLength={120}
          />
        </Field>
        <Field label={t('agent.withdrawal.field.whatsapp.label')}>
          <input
            className={`${inputCls} font-mono`}
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder={t('agent.withdrawal.field.whatsapp.placeholder')}
            maxLength={40}
          />
        </Field>
        <Field label={t('agent.withdrawal.field.note.label')}>
          <input
            className={inputCls}
            value={note} maxLength={190}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('agent.withdrawal.field.note.placeholder')}
          />
        </Field>

        {err && (
          <div className="rounded-xl bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-400/30 text-rose-700 dark:text-rose-200 px-3 py-2 text-[12.5px]">
            {err}
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-2 rounded-full text-[12px] font-semibold
                       bg-white/60 dark:bg-arena-700/40
                       text-arena-700 dark:text-neon-200/80
                       border border-arena-200/60 dark:border-arena-600/50
                       hover:bg-white/90 dark:hover:bg-arena-600/55 transition"
          >
            {t('agent.withdrawal.cta.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !amount || (paymentMethod === 'upi' && !upi)}
            className={`ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                       text-white text-[13px] font-bold
                       transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
                       ${target === 'deposit'
                         ? 'bg-amber-500 hover:bg-amber-400 active:bg-amber-600 shadow-[0_2px_10px_rgba(245,158,11,0.4)]'
                         : 'bg-brand-500 hover:bg-brand-400 active:bg-brand-600 shadow-[0_2px_10px_rgba(var(--brand-rgb),0.4)]'}`}
          >
            {busy
              ? <Icon name="progress_activity" size={14} className="animate-spin" />
              : <Icon name="send" size={14} />}
            {t('agent.withdrawal.cta.submit')}
          </button>
        </div>
      </div>
    </motion.form>
  );
}

function PayMethodPill({
  active, onClick, icon, label, note,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  note: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl px-3 py-2 text-left border-2 transition
        ${active
          ? 'border-brand-400 bg-brand-50/60 dark:bg-brand-400/10 dark:border-brand-400/60 shadow-[0_2px_10px_rgba(var(--brand-rgb),0.2)]'
          : 'border-arena-200/60 dark:border-arena-600/50 bg-white dark:bg-arena-700/30 hover:border-arena-300 dark:hover:border-arena-500/60'}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`grid place-items-center w-7 h-7 rounded-lg
                          ${active
                            ? 'bg-brand-500 text-white'
                            : 'bg-arena-100 dark:bg-arena-700/60 text-arena-700 dark:text-neon-200/85'}`}>
          <Icon name={icon} size={15} filled />
        </span>
        <span className={`text-[12.5px] font-bold ${active ? 'text-arena-900 dark:text-white' : 'text-arena-700 dark:text-neon-200/80'}`}>
          {label}
        </span>
      </div>
      <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 mt-1">{note}</div>
    </button>
  );
}

const inputCls = `w-full rounded-xl px-3 py-2 text-[14px]
                  bg-arena-50/40 dark:bg-arena-950/40
                  border border-arena-200/60 dark:border-arena-600/50
                  text-arena-900 dark:text-white
                  placeholder-arena-400 dark:placeholder-neon-200/35
                  outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30`;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
        {label}
      </span>
      {children}
    </label>
  );
}
